import { useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type { Task } from '../../types/tasks';
import { useTaskStore } from '../../stores/taskStore';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';

const CADENCE_ORDER = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'trigger'] as const;

const CADENCE_STYLES: Record<string, string> = {
  daily: 'bg-accent-purple/15 text-accent-purple-light',
  weekly: 'bg-info/15 text-info',
  biweekly: 'bg-avatar-cyan/15 text-avatar-cyan',
  monthly: 'bg-success/15 text-success',
  quarterly: 'bg-warning/15 text-warning',
  trigger: 'bg-error/15 text-error',
};

const STATUS_INDICATOR: Record<string, string> = {
  backlog: 'bg-text-muted',
  in_progress: 'bg-info',
  completed: 'bg-success',
};

interface RecurringTabProps {
  onTaskClick: (task: Task) => void;
}

interface CadenceGroup {
  cadence: string;
  label: string;
  tasks: Task[];
}

export default function RecurringTab({ onTaskClick }: RecurringTabProps) {
  const tasks = useTaskStore((s) => s.tasks);
  const allTasks = useTaskStore((s) => s.tasks);
  const [showCompleted, setShowCompleted] = useState(false);

  // We need all tasks (including daily) for the recurring view,
  // but the store may filter by cadence. We'll use what's available.
  const recurringTasks = useMemo(() => {
    return allTasks.filter((t) => {
      if (!t.cadence) return false;
      if (!showCompleted && t.status === 'completed') return false;
      return true;
    });
  }, [allTasks, showCompleted]);

  const groups = useMemo(() => {
    const result: CadenceGroup[] = [];

    for (const cadence of CADENCE_ORDER) {
      const cadenceTasks = recurringTasks.filter((t) => t.cadence === cadence);
      if (cadenceTasks.length > 0) {
        result.push({
          cadence,
          label: cadence.charAt(0).toUpperCase() + cadence.slice(1),
          tasks: cadenceTasks,
        });
      }
    }

    return result;
  }, [recurringTasks]);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-text-muted">
          {recurringTasks.length} recurring task{recurringTasks.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors"
        >
          {showCompleted ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showCompleted ? 'Hide Completed' : 'Show Completed'}
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12 text-text-muted text-sm">
          No recurring tasks found. Tasks with a cadence will appear here.
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <div key={group.cadence}>
              {/* Group Header */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CADENCE_STYLES[group.cadence]}`}>
                  {group.label}
                </span>
                <span className="text-xs text-text-muted">
                  {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Task Rows */}
              <div className="bg-bg-secondary border border-border-subtle rounded-lg overflow-hidden divide-y divide-border-subtle">
                {group.tasks.map((task) => {
                  const agent = task.assigned_agent ? getAgentBySlug(task.assigned_agent) : null;
                  const colorClass = agent ? getAgentColor(agent.department) : 'bg-bg-tertiary';

                  return (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick(task)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-bg-tertiary/50 cursor-pointer transition-colors"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onTaskClick(task);
                        }
                      }}
                    >
                      {/* Status dot */}
                      <div className={`h-2 w-2 rounded-full shrink-0 ${STATUS_INDICATOR[task.status] || 'bg-text-muted'}`} />

                      {/* Title */}
                      <span className="text-sm text-text-primary truncate flex-1">{task.title}</span>

                      {/* Agent */}
                      {agent ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white ${colorClass}`}>
                            {agent.name.charAt(0)}
                          </div>
                          <span className="text-xs text-text-secondary hidden sm:inline">{agent.name}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-text-muted shrink-0">Unassigned</span>
                      )}

                      {/* Status badge */}
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                        task.status === 'completed'
                          ? 'bg-success/15 text-success'
                          : task.status === 'in_progress'
                          ? 'bg-info/15 text-info'
                          : 'bg-bg-tertiary text-text-muted'
                      }`}>
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
